import React from 'react';
import { FiMail } from 'react-icons/fi';

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  recipients: string;
  openRate: string;
  clickRate: string;
  triggerDate: string;
  action: string;
  subtitle?: string;
  segment?: string;
  tags?: string[];
  deliveredDate?: string;
  stats?: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    spamReported: number;
    openRate: string;
    clickRate: string;
    newSubscribers: number;
    unsubscribes: number;
  };
  lastOpened?: { name: string; time: string };
  lastClicked?: { name: string; time: string };
}

interface Metrics {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  openRate: number;
  clickRate: number;
  bounced: number;
  unsubscribed: number;
}

// Typecast icon component for TypeScript compatibility
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Renders a stat box for campaign metrics
const StatBox: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => {
  // Special styling for New Subscribers and Unsubscribes
  if (label === 'New Subscribers' || label === 'Unsubscribes') {
    return (
      <div
        className={`bg-white ${label === 'New Subscribers' ? 'rounded-t' : 'rounded-b'
          } p-[13.5px] flex items-center justify-between border border-gray-200 shadow-sm`}
      >
        <div className='flex justify-between items-center w-[100%]'>
          <h3 className='text-[#18181B] text-[14px] font-semibold'>{label}</h3>
          <p className='text-[20px] text-[#3F5265] font-semibold'>{value}</p>
        </div>
      </div>
    );
  }

  // Special styling for Last Opened and Last Clicked
  if (label === 'Last Opened' || label === 'Last Clicked') {
    return (
      <div className='bg-white p-[18px] justify-between flex items-center border border-gray-200 shadow-sm'>
        <p className='text-[#18181B] text-[14px] font-semibold'>{label}</p>
        <div className='flex items-center gap-2'>
          {
            value === 'N/A' ? (
              ""
            ) : <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                value as string
              )}&background=random`}
              alt={value as string}
              className='w-8 h-8 rounded-full'
            />

          }

          <div>
            <p className='font-medium text-[#18181B] text-[14px]'>
              {value}
            </p>
            {/* <p className='text-[#657584] text-[12px] text-xs'>
              {label === 'Last Opened' ? 'Opened at 14:30' : 'Clicked at 15:45'}
            </p> */}
          </div>
        </div>
      </div>
    );
  }

  // Default stat box
  return (
    <div className='bg-white rounded-md p-3 flex items-center justify-between border border-gray-200 shadow-sm'>
      <div>
        <h3 className='text-[#657584] text-[14px] mb-2 font-semibold'>
          {label}
        </h3>
        <p className='text-[28px] text-[#3F5265] font-semibold'>{value}</p>
      </div>
      <div style={{ height: '-webkit-fill-available' }}>
        <span className='flex items-center justify-center mr-2 w-[30px] h-[30px] rounded-[5px] bg-[#FFF1EC]'>
          <FiMailComponent className='text-orange-500 text-2xl' size={17} />
        </span>
      </div>
    </div>
  );
};

interface OverviewProps {
  campaign: any | Campaign;
  metrics?: Metrics;
}

// Renders the overview tab for a campaign
const Overview: React.FC<OverviewProps> = ({ campaign, metrics }) => {
  // const [filter, setFilter] = useState<'24h' | '7d'>('24h');

  // ⭐ Use metrics from props if available, otherwise use campaign.stats or defaults
  const stats = metrics || campaign?.stats || {
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    spamReported: 0,
    openRate: '0%',
    clickRate: '0%',
    newSubscribers: 0,
    unsubscribes: 0,
  };

  // Format metrics for display
  // const formatMetricValue = (value: number, label: string) => {
  //   if (label === 'Open Rate' || label === 'Click Rate') {
  //     return `${value}%`;
  //   }
  //   return value;
  // };

  const lastOpened = campaign?.lastOpened || 'NA';
  const lastClicked = campaign?.lastClicked || 'NA';

  // Generates sample data for 24-hour engagement chart
  // const engagementData24h = Array.from({ length: 24 }, (_, i) => ({
  //   hour: `${i.toString().padStart(2, '0')}:00`,
  //   open: Math.floor(stats.opened / 24 + Math.random() * 10),
  //   click: Math.floor(stats.clicked / 24 + Math.random() * 5),
  // }));

  // Generates sample data for 7-day engagement chart
  // const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // const engagementData7d = days.map((day) => ({
  //   day,
  //   open: Math.floor(stats.opened / 7 + Math.random() * 20),
  //   click: Math.floor(stats.clicked / 7 + Math.random() * 10),
  // }));

  // Selects dataset based on filter
  // const engagementData = filter === '24h' ? engagementData24h : engagementData7d;

  // const series =
  //   filter === '24h'
  //     ? [
  //       { name: 'Open', data: engagementData.map((d) => d.open) },
  //       { name: 'Click', data: engagementData.map((d) => d.click) },
  //     ]
  //     : [
  //       { name: 'Open', data: engagementData.map((d) => d.open) },
  //       { name: 'Click', data: engagementData.map((d) => d.click) },
  //     ];

  // Configures ApexCharts options for the engagement line chart
  // const options: ApexCharts.ApexOptions = {
  //   chart: { type: 'line', toolbar: { show: false } },
  //   stroke: { curve: 'smooth', width: 3 },
  //   xaxis: {
  //     categories: engagementData.map((d) =>
  //       filter === '24h'
  //         ? (d as { hour: string }).hour
  //         : (d as { day: string }).day
  //     ),
  //     labels: { style: { fontSize: '12px', colors: '#657584' } },
  //   },
  //   yaxis: { labels: { style: { fontSize: '12px', colors: '#657584' } } },
  //   colors: ['#FF5733', '#008FFB'],
  //   legend: { position: 'top', horizontalAlign: 'left' },
  //   markers: { size: 4 },
  //   grid: { borderColor: '#f1f1f1' },
  // };

  return (
    <>
      {/* Stats */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
        <StatBox label='Sent' value={stats.sent || 0} />
        <StatBox label='Delivered' value={stats.delivered || 0} />
        <StatBox label='Opened' value={stats.opened || 0} />
        <StatBox label='Clicked' value={stats.clicked || 0} />
        <StatBox label='Bounced' value={stats.bounced || 0} />
        <StatBox label='Spam Reported' value={stats.spamReported || 0} />
        <StatBox label='Open Rate' value={stats.openRate || '0%'} />
        <StatBox label='Click Rate' value={stats.clickRate || '0%'} />
      </div>

      {/* Subscribers + Activity */}
      <div className='grid grid-cols-2 md:grid-cols-2 gap-4 mb-6'>
        <div>
          <StatBox label='New Subscribers' value={stats.newSubscribers || 0} />
          <StatBox label='Unsubscribes' value={stats.unsubscribes || 0} />
        </div>

        <div>
          <StatBox label='Last Opened' value={lastOpened === 'NA' ? 'N/A' : lastOpened.name} />
          <StatBox label='Last Clicked' value={lastClicked === 'NA' ? 'N/A' : lastClicked.name} />
        </div>
      </div>

      {/* Engagement Chart - Commented out for now */}
      {/* <div className='bg-white p-4 rounded-lg shadow border'>
        <div className='flex justify-between items-center mb-4'>
          <h3 className='text-lg font-semibold text-gray-800'>Engagement</h3>
          <select
            className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265]'
            value={filter}
            onChange={(e) => setFilter(e.target.value as '24h' | '7d')}
          >
            <option value='24h'>Last 24 Hours</option>
            <option value='7d'>Last 7 Days</option>
          </select>
        </div>
        <Chart options={options} series={series} type='line' height={300} />
      </div> */}
    </>
  );
};

export default Overview;